/**
 * Tabla "Detalle por componente — de donde viene cada peso" de la pestaña Estadísticas.
 *
 * Réplica de `statsTablaModelos` del SSR (`cotizador_puertas.html`) con los primitivos de
 * tabla del frontend: una fila por componente cobrado descompuesto en costo de material y
 * ganancia (refuerzos y tubos solo si > 0), más "Dobleces adicionales", "IVA" y la fila
 * TOTAL destacada. Cierra con el **footer resumen** (Cobras | Pagas material | Pagas IVA |
 * Te queda). Sin lógica de datos: deriva de `CalcResult` con `buildComponentRows` y formatea.
 */
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";

import { formatMoney, formatPercent } from "../format";
import { buildComponentRows } from "../statsDerive";
import type { CalcResult } from "../types";

interface StatsComponentTableProps {
  /** Última cotización calculada (alimenta las filas de la tabla y el footer). */
  calc: CalcResult;
}

export function StatsComponentTable({ calc }: StatsComponentTableProps) {
  const rows = buildComponentRows(calc);
  const stats = calc.stats_desktop;
  const totalCobrado = Number(calc.total || 0);
  const costoRealLam = Number(stats?.costo_real_lam || 0);
  const ivaTotal = Number(stats?.iva_total || 0);
  const gananciaTotal = Number(stats?.ganancia_total || 0);
  const margenPct = Number(stats?.margen_pct || 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Detalle por componente — de dónde viene cada peso</CardTitle>
      </CardHeader>
      <CardContent className="px-0 py-0">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Concepto</TableHeaderCell>
              <TableHeaderCell className="text-right">Cobrado al cliente</TableHeaderCell>
              <TableHeaderCell className="text-right">Costo de material</TableHeaderCell>
              <TableHeaderCell className="text-right">Te queda a ti</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) =>
              row.isTotal ? (
                <TableRow key={row.concepto} className="bg-slate-50 font-bold hover:bg-slate-50">
                  <TableCell className="font-bold text-slate-900">{row.concepto}</TableCell>
                  <TableCell className="text-right font-bold text-slate-900">
                    {formatMoney(row.cobrado)}
                  </TableCell>
                  <TableCell className="text-right font-bold text-cyan-600">
                    {formatMoney(row.costo)}
                  </TableCell>
                  <TableCell className="text-right font-bold text-emerald-600">
                    {formatMoney(row.ganancia)}
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={row.concepto}>
                  <TableCell>{row.concepto}</TableCell>
                  <TableCell className="text-right text-slate-900">
                    {formatMoney(row.cobrado)}
                  </TableCell>
                  <TableCell
                    className={`text-right ${row.costo > 0 ? "text-cyan-600" : "text-slate-400"}`}
                  >
                    {formatMoney(row.costo)}
                  </TableCell>
                  <TableCell
                    className={`text-right ${row.ganancia > 0 ? "text-emerald-600" : "text-slate-400"}`}
                  >
                    {formatMoney(row.ganancia)}
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
        <p className="border-t-4 border-t-emerald-500 px-5 py-4 text-sm font-bold text-emerald-600">
          Cobras {formatMoney(totalCobrado)} | Pagas material {formatMoney(costoRealLam)} | Pagas
          IVA {formatMoney(ivaTotal)} | Te queda {formatMoney(gananciaTotal)} (
          {formatPercent(margenPct)})
        </p>
      </CardContent>
    </Card>
  );
}
